import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import ExpandableSection from "@/components/job/ExpandableSection";
import PhoneInput from "@/components/PhoneInput";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/ui/textInput";
import { Typography } from "@/components/ui/typography";
import useCUMechanicalJobStore from "@/store/useCUMechanicalJobStore";

const contactInformationSchema = z
  .object({
    contact_info_name: z.string().max(30).optional(),
    contact_info_email: z.string().email().optional().or(z.literal("")),
  })
  .partial({ contact_info_email: true });

type ContactInformationSchema = z.infer<typeof contactInformationSchema>;
const cardName = "contactInformation";
export function ContactInformation() {
  const { sections, toggleExpandableSection, setContactInformation } = useCUMechanicalJobStore();
  const savedContactInformation = sections.contactInformation;
  const contactInfoData = savedContactInformation.data;
  const hasContactInfo =
    Boolean(contactInfoData.contact_info_name) ||
    Boolean(contactInfoData.contact_info_email) ||
    Boolean(contactInfoData.contact_info_phone);
  const [addMore, setAddMore] = useState(Boolean(savedContactInformation.addMore) || hasContactInfo);
  const [contact_info_phone, setContact_Info_Phone] = useState(contactInfoData.contact_info_phone ?? "");

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<ContactInformationSchema>({
    resolver: zodResolver(contactInformationSchema),
    values: {
      contact_info_name: contactInfoData.contact_info_name ? contactInfoData.contact_info_name : "",
      contact_info_email: contactInfoData.contact_info_email ? contactInfoData.contact_info_email : "",
    },
  });

  useEffect(() => {
    const nextAddMore = Boolean(savedContactInformation.addMore) || hasContactInfo;
    setAddMore(nextAddMore);
  }, [savedContactInformation.addMore, hasContactInfo]);

  useEffect(() => {
    const nextPhone = contactInfoData.contact_info_phone ?? "";
    if (nextPhone !== contact_info_phone) {
      setContact_Info_Phone(nextPhone);
    }
  }, [contactInfoData.contact_info_phone, contact_info_phone]);

  return (
    <ExpandableSection
      status={savedContactInformation.status}
      setIsExpanded={() => toggleExpandableSection(cardName)}
      title="Contact Information"
    >
      {addMore ? (
        <form
          onSubmit={handleSubmit((data) => {
            setContactInformation({ data: { ...data, contact_info_phone }, status: "done" });
          })}
          className="flex flex-col gap-2"
        >
          <div className="flex flex-wrap gap-2 ">
            <TextInput
              className="flex-1 basis-full md:basis-56"
              label="Contact Name"
              type="text"
              placeholder="Enter your name"
              error={errors.contact_info_name?.message}
              {...register("contact_info_name")}
            />
            <PhoneInput
              className="flex-1 basis-full md:basis-56"
              value={contact_info_phone || "+"}
              onChange={(phone: string) => setContact_Info_Phone(phone)}
            />
            <TextInput
              className="flex-1 basis-full md:basis-56"
              label="Email"
              type="email"
              placeholder="Enter your email"
              error={errors.contact_info_email?.message}
              {...register("contact_info_email")}
            />
          </div>
          <Button type="submit" className="self-end">
            Save
          </Button>
        </form>
      ) : (
        <div className="flex flex-col gap-2">
          <Typography variant="text-xl" weight="semibold" className="text-center">
            Do you wish to provide Contact details such as Contact name, phone number, and email?
          </Typography>
          <div className="flex justify-center gap-2">
            <Button variant="outline" onClick={() => setContactInformation({ status: "done" })}>
              No
            </Button>
            <Button onClick={() => setAddMore(true)}>Yes</Button>
          </div>
        </div>
      )}
    </ExpandableSection>
  );
}
